import { useState, useEffect, useMemo } from 'react'
import {
  CheckCircle2,
  AlertTriangle,
  Download,
  RotateCcw,
  Check,
  History,
  Search,
  Filter,
  ArrowRight,
  AlertCircle,
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Button } from '@/components/ui/Button'
import { StatusBadge, type BadgeTone } from '@/components/ui/StatusBadge'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import {
  bomStandardizationService,
  type BomReviewLineProposal,
  type BomReviewGroupSummary,
  type BomReviewResult,
  type BomNameChangeBatch,
  type ConfidenceLevel,
} from '@/services/bomStandardization'
import { useCan } from '@/features/auth/AuthContext'
import { formatDateFull } from '@/lib/dateFormat'

export function BomReviewNamesPage() {
  const canWriteBom = useCan('bom.write')
  const canAdminUsers = useCan('admin.users')
  const canWrite = canWriteBom || canAdminUsers
  const [activeTab, setActiveTab] = useState<'proposals' | 'history'>('proposals')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [proposals, setProposals] = useState<BomReviewLineProposal[]>([])
  const [groups, setGroups] = useState<BomReviewGroupSummary[]>([])
  const [stats, setStats] = useState<BomReviewResult['stats'] | null>(null)

  // Filters
  const [confidenceFilter, setConfidenceFilter] = useState<string>('')
  const [bomTypeFilter, setBomTypeFilter] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')

  // Selected line IDs
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Apply Modal state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)
  const [applyBatchName, setApplyBatchName] = useState('')
  const [applyNotes, setApplyNotes] = useState('')
  const [applying, setApplying] = useState(false)
  const [applySuccessMessage, setApplySuccessMessage] = useState<string | null>(null)

  // History state
  const [batches, setBatches] = useState<BomNameChangeBatch[]>([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [revertingBatchId, setRevertingBatchId] = useState<string | null>(null)

  async function loadReview() {
    setLoading(true)
    setError(null)
    try {
      const res = await bomStandardizationService.review()
      setProposals(res.lines)
      setGroups(res.groups)
      setStats(res.stats)

      // Pre-select ONLY CONFIDENT lines
      const preSelected = new Set<string>()
      for (const line of res.lines) {
        if (line.confidence === 'CONFIDENT') {
          preSelected.add(line.id)
        }
      }
      setSelectedIds(preSelected)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được dữ liệu rà soát BOM')
    } finally {
      setLoading(false)
    }
  }

  async function loadBatches() {
    setLoadingBatches(true)
    try {
      const list = await bomStandardizationService.listBatches()
      setBatches(list)
    } catch {
      // Best effort load history
    } finally {
      setLoadingBatches(false)
    }
  }

  useEffect(() => {
    void loadReview()
    void loadBatches()
  }, [])

  // Filtered lines
  const filteredLines = useMemo(() => {
    return proposals.filter((p) => {
      if (confidenceFilter && p.confidence !== confidenceFilter) return false
      if (bomTypeFilter && p.bom_type !== bomTypeFilter) return false
      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        const matchCode = p.product_code.toLowerCase().includes(term)
        const matchName = p.product_name.toLowerCase().includes(term)
        const matchCur = p.current_name.toLowerCase().includes(term)
        const matchProp = p.proposed_name.toLowerCase().includes(term)
        if (!matchCode && !matchName && !matchCur && !matchProp) return false
      }
      return true
    })
  }, [proposals, confidenceFilter, bomTypeFilter, searchTerm])

  // Selected stats
  const selectedLinesList = useMemo(() => {
    return proposals.filter((p) => selectedIds.has(p.id))
  }, [proposals, selectedIds])

  const selectedProductsCount = useMemo(() => {
    return new Set(selectedLinesList.map((p) => p.product_id)).size
  }, [selectedLinesList])

  const selectedPriceWarnings = useMemo(() => {
    return selectedLinesList.filter((p) => p.price_warning)
  }, [selectedLinesList])

  function toggleSelectLine(id: string) {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelectedIds(next)
  }

  function toggleSelectGroup(group: BomReviewGroupSummary, select: boolean) {
    const next = new Set(selectedIds)
    for (const p of proposals) {
      if (
        p.bom_type === group.bom_type &&
        p.current_name === group.old_name &&
        p.proposed_name === group.proposed_name
      ) {
        if (select) {
          next.add(p.id)
        } else {
          next.delete(p.id)
        }
      }
    }
    setSelectedIds(next)
  }

  function isGroupFullySelected(group: BomReviewGroupSummary): boolean {
    const matching = proposals.filter(
      (p) =>
        p.bom_type === group.bom_type &&
        p.current_name === group.old_name &&
        p.proposed_name === group.proposed_name,
    )
    if (matching.length === 0) return false
    return matching.every((p) => selectedIds.has(p.id))
  }

  function handleSelectAllVisible(select: boolean) {
    const next = new Set(selectedIds)
    for (const p of filteredLines) {
      if (select) {
        // Only select lines that have a proposed standard name
        if (p.confidence !== 'UNKNOWN') {
          next.add(p.id)
        }
      } else {
        next.delete(p.id)
      }
    }
    setSelectedIds(next)
  }

  function exportToExcel() {
    const rows = filteredLines.map((p, idx) => ({
      STT: idx + 1,
      'Mã sản phẩm': p.product_code,
      'Tên sản phẩm': p.product_name,
      'Nhóm BOM': p.bom_type === 'HARDWARE' ? 'Ngũ kim' : p.bom_type === 'WOOD' ? 'Gỗ' : 'Đóng gói',
      'Tên hiện tại': p.current_name,
      'Tên chuẩn đề xuất': p.proposed_name,
      'Quy cách hiện tại': p.spec || '',
      'Quy cách đề xuất': p.proposed_spec || p.spec || '',
      'Màu hiện tại': p.color || '',
      'Màu đề xuất': p.proposed_color || '',
      ĐVT: p.uom || '',
      'Số lượng': p.qty_per || 1,
      'Mức tin cậy':
        p.confidence === 'CONFIDENT'
          ? 'Chắc chắn'
          : p.confidence === 'NEEDS_REVIEW'
            ? 'Cần xem'
            : 'Không rõ',
      'Lý do khớp': p.confidence_reason,
      'Cảnh báo giá': p.price_warning || 'Không đổi',
    }))

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.book_append_sheet(wb, ws, 'De_xuat_chuan_hoa_BOM')
    XLSX.writeFile(wb, `De_xuat_chuan_hoa_BOM_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  async function handleConfirmApply() {
    if (selectedLinesList.length === 0) return
    setApplying(true)
    try {
      const payload = {
        batch_name:
          applyBatchName.trim() ||
          `Lô chuẩn hóa ${selectedLinesList.length} dòng (${formatDateFull(new Date())})`,
        lines: selectedLinesList.map((p) => ({
          bom_type: p.bom_type,
          config_id: p.config_id,
          product_id: p.product_id,
          line_id: p.line_id,
          field_name: p.field_name,
          old_name: p.current_name,
          new_name: p.proposed_name,
          spec: p.spec,
          old_spec: p.spec,
          new_spec: p.proposed_spec,
          old_color: p.color,
          new_color: p.proposed_color,
        })),
        notes: applyNotes.trim() || undefined,
      }

      const res = await bomStandardizationService.applyChanges(payload)
      setIsApplyModalOpen(false)
      setApplySuccessMessage(
        `Áp dụng thành công lô '${res.batch.batch_name}': Đã chuẩn hóa ${res.applied_lines_count} dòng trong ${selectedProductsCount} mã hàng.`,
      )
      await loadReview()
      await loadBatches()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi áp dụng thay đổi BOM')
    } finally {
      setApplying(false)
    }
  }

  async function handleRevertBatch(batchId: string) {
    if (
      !confirm(
        'Bạn có chắc chắn muốn hoàn tác toàn bộ các dòng của lô này về tên cũ? Dòng đã bị sửa tiếp sau đó sẽ được giữ nguyên.',
      )
    )
      return
    setRevertingBatchId(batchId)
    try {
      const res = await bomStandardizationService.revertBatch(batchId)
      alert(
        `Đã hoàn tác lô thành công: ${res.reverted_count} dòng đã hoàn tác về tên cũ. ${
          res.skipped_count > 0 ? `Bỏ qua ${res.skipped_count} dòng do đã sửa đổi sau đó.` : ''
        }`,
      )
      await loadReview()
      await loadBatches()
    } catch (err: any) {
      alert(err.message || 'Lỗi khi hoàn tác lô')
    } finally {
      setRevertingBatchId(null)
    }
  }

  const confidenceBadgeTone: Record<ConfidenceLevel, BadgeTone> = {
    CONFIDENT: 'success',
    NEEDS_REVIEW: 'warning',
    UNKNOWN: 'neutral',
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <Breadcrumb
            items={[
              { label: 'Trang chủ', to: '/' },
              { label: 'BOM', to: '/purchasing/boms' },
              { label: 'Rà soát chuẩn hóa tên', to: '/boms/review-names' },
            ]}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <PageHeader
              title="Rà soát & Chuẩn hóa tên trong BOM"
              subtitle="Công cụ rà soát tự động, phân loại mức tin cậy và chuẩn hóa tên hàng loạt có xem trước & hoàn tác"
            />
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={exportToExcel}>
                <Download className="mr-1.5 h-4 w-4" />
                Xuất Excel đề xuất
              </Button>
              {canWrite && (
                <Button
                  variant="primary"
                  disabled={selectedLinesList.length === 0}
                  onClick={() => {
                    setApplyBatchName('')
                    setApplyNotes('')
                    setIsApplyModalOpen(true)
                  }}
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Áp dụng đã chọn ({selectedLinesList.length})
                </Button>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mt-4 flex gap-4 border-b border-slate-200 text-xs font-medium">
            <button
              onClick={() => setActiveTab('proposals')}
              className={`pb-2.5 transition-colors ${
                activeTab === 'proposals'
                  ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Danh sách đề xuất ({proposals.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-2.5 transition-colors flex items-center gap-1.5 ${
                activeTab === 'history'
                  ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <History className="h-3.5 w-3.5" />
              Lịch sử & Hoàn tác lô ({batches.length})
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {applySuccessMessage && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>{applySuccessMessage}</span>
            </div>
            <button onClick={() => setApplySuccessMessage(null)} className="text-emerald-500 hover:text-emerald-800">
              ✕
            </button>
          </div>
        )}

        {/* Tab 1: Proposals */}
        {activeTab === 'proposals' && (
          <>
            {/* KPI Cards */}
            <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiCard
                label="Chắc chắn (tích sẵn)"
                value={stats?.confident_count ?? 0}
                tone="success"
              />
              <KpiCard
                label="Cần xem (chưa tích)"
                value={stats?.needs_review_count ?? 0}
                tone="warning"
              />
              <KpiCard
                label="Không rõ trong từ điển"
                value={stats?.unknown_count ?? 0}
                tone="default"
              />
              <KpiCard
                label="Mã sản phẩm ảnh hưởng"
                value={stats?.affected_products_count ?? 0}
                tone="default"
              />
            </div>

            {/* Filter toolbar */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 mr-1">
                  <Filter className="h-3 w-3" /> Lọc:
                </span>
                <select
                  value={confidenceFilter}
                  onChange={(e) => setConfidenceFilter(e.target.value)}
                  className="h-8 rounded border border-slate-200 bg-white px-2 text-xs outline-none focus:border-blue-500"
                >
                  <option value="">Tất cả mức tin cậy</option>
                  <option value="CONFIDENT">Chắc chắn</option>
                  <option value="NEEDS_REVIEW">Cần xem</option>
                  <option value="UNKNOWN">Không rõ</option>
                </select>

                <select
                  value={bomTypeFilter}
                  onChange={(e) => setBomTypeFilter(e.target.value)}
                  className="h-8 rounded border border-slate-200 bg-white px-2 text-xs outline-none focus:border-blue-500"
                >
                  <option value="">Tất cả loại BOM</option>
                  <option value="HARDWARE">Ngũ kim</option>
                  <option value="WOOD">Gỗ</option>
                  <option value="FOAM">Đóng gói</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative min-w-[220px]">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm mã hàng, tên cũ/mới..."
                    className="h-8 w-full rounded border border-slate-200 pl-8 pr-3 text-xs outline-none focus:border-blue-500"
                  />
                </div>

                <Button variant="ghost" size="sm" onClick={() => handleSelectAllVisible(true)}>
                  Chọn tất cả
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleSelectAllVisible(false)}>
                  Bỏ chọn tất cả
                </Button>
              </div>
            </div>

            {/* Selection summary banner */}
            <div className="mb-3 flex items-center justify-between rounded border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs text-blue-900">
              <div className="flex items-center gap-2">
                <span className="font-semibold">
                  Đã chọn {selectedLinesList.length} / {proposals.length} dòng
                </span>
                <span className="text-blue-600">({selectedProductsCount} mã hàng)</span>
                {selectedPriceWarnings.length > 0 && (
                  <span className="flex items-center gap-1 text-amber-700 font-medium">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                    Có {selectedPriceWarnings.length} dòng tác động đến khớp giá
                  </span>
                )}
              </div>
              {canWrite && selectedLinesList.length > 0 && (
                <button
                  onClick={() => setIsApplyModalOpen(true)}
                  className="font-semibold text-blue-700 hover:underline"
                >
                  Tiến hành áp dụng →
                </button>
              )}
            </div>

            {/* Groups overview */}
            {groups.length > 0 && (
              <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <h4 className="mb-2 text-xs font-semibold text-slate-700">
                  Gom nhóm theo cặp tên đề xuất ({groups.length} cặp tên)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {groups.slice(0, 15).map((g, i) => {
                    const isFullySel = isGroupFullySelected(g)
                    return (
                      <button
                        key={i}
                        onClick={() => toggleSelectGroup(g, !isFullySel)}
                        className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition-colors ${
                          isFullySel
                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <span className="line-through text-slate-400">{g.old_name}</span>
                        <ArrowRight className="h-3 w-3 text-slate-400" />
                        <span className="font-semibold">{g.proposed_name}</span>
                        <span className="rounded bg-white px-1.5 py-0.2 text-[10px] text-slate-500 border border-slate-200">
                          {g.total_lines}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Proposals Table */}
            {loading ? (
              <PageLoading label="Đang rà soát toàn bộ dữ liệu BOM..." />
            ) : error ? (
              <PageError message={error} onRetry={loadReview} />
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                    <thead className="bg-slate-50 font-semibold text-slate-600">
                      <tr>
                        <th className="w-10 px-3 py-2.5 text-center">
                          <input
                            type="checkbox"
                            checked={
                              filteredLines.length > 0 &&
                              filteredLines.every((p) => selectedIds.has(p.id))
                            }
                            onChange={(e) => handleSelectAllVisible(e.target.checked)}
                            className="rounded border-slate-300 text-blue-600"
                          />
                        </th>
                        <th className="w-12 px-3 py-2.5 text-center">STT</th>
                        <th className="px-3 py-2.5">Mã hàng</th>
                        <th className="px-3 py-2.5">Nhóm</th>
                        <th className="px-3 py-2.5">Tên hiện tại</th>
                        <th className="px-3 py-2.5">Tên chuẩn đề xuất</th>
                        <th className="px-3 py-2.5">Quy cách</th>
                        <th className="px-3 py-2.5 text-center">Mức tin cậy</th>
                        <th className="px-3 py-2.5">Tác động giá</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredLines.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-slate-400">
                            Không có dòng nào phù hợp với bộ lọc
                          </td>
                        </tr>
                      ) : (
                        filteredLines.map((line, idx) => {
                          const isSelected = selectedIds.has(line.id)
                          return (
                            <tr
                              key={line.id}
                              onClick={() => toggleSelectLine(line.id)}
                              className={`cursor-pointer transition-colors ${
                                isSelected ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-slate-50/80'
                              }`}
                            >
                              <td
                                className="px-3 py-2.5 text-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleSelectLine(line.id)}
                                  className="rounded border-slate-300 text-blue-600"
                                />
                              </td>
                              <td className="px-3 py-2.5 text-center font-medium text-slate-400 tabular-nums">
                                {idx + 1}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="font-semibold text-slate-900">{line.product_code}</div>
                                <div className="text-[11px] text-slate-400 line-clamp-1">{line.product_name}</div>
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                                  {line.bom_type === 'HARDWARE'
                                    ? 'Ngũ kim'
                                    : line.bom_type === 'WOOD'
                                      ? 'Gỗ'
                                      : 'Đóng gói'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-slate-700">
                                <span className="rounded bg-red-50/60 px-1.5 py-0.5 text-red-900 line-through border border-red-200/40">
                                  {line.current_name}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1">
                                  <ArrowRight className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                                  <span className="font-semibold text-emerald-800">
                                    {line.proposed_name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-slate-600 font-mono text-[11px]">
                                {line.proposed_spec && line.proposed_spec !== line.spec ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-slate-400 line-through">{line.spec || '(trống)'}</span>
                                    <ArrowRight className="h-3 w-3 text-blue-600 flex-shrink-0" />
                                    <span className="font-semibold text-blue-800">{line.proposed_spec}</span>
                                  </div>
                                ) : (
                                  line.spec || '—'
                                )}
                                {line.proposed_color !== undefined && line.proposed_color !== line.color && (
                                  <div className="text-[10px] text-amber-700 mt-0.5">
                                    Màu: <span className="line-through">{line.color}</span> → {line.proposed_color || '(bỏ màu)'}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                <StatusBadge
                                  tone={confidenceBadgeTone[line.confidence]}
                                  label={
                                    line.confidence === 'CONFIDENT'
                                      ? 'Chắc chắn'
                                      : line.confidence === 'NEEDS_REVIEW'
                                        ? 'Cần xem'
                                        : 'Không rõ'
                                  }
                                />
                              </td>
                              <td className="px-3 py-2.5">
                                {line.price_warning ? (
                                  <span
                                    className="flex items-center gap-1 text-[11px] font-medium text-amber-700"
                                    title={line.price_warning}
                                  >
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                                    {line.price_warning}
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-slate-400">Không đổi giá</span>
                                )}
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Tab 2: History & Revert Batches */}
        {activeTab === 'history' && (
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Lịch sử các lô chuẩn hóa tên BOM</h3>
                <p className="text-xs text-slate-500">
                  Mỗi lần bấm Áp dụng tạo một lô giao dịch độc lập. Có thể hoàn tác an toàn bất kỳ lúc nào.
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={loadBatches}>
                Làm mới lịch sử
              </Button>
            </div>

            {loadingBatches ? (
              <PageLoading label="Đang tải lịch sử các lô..." />
            ) : batches.length === 0 ? (
              <div className="py-12 text-center text-xs text-slate-400">
                Chưa có lô chuẩn hóa tên BOM nào được thực hiện
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                  <thead className="bg-slate-50 font-semibold text-slate-600">
                    <tr>
                      <th className="w-12 px-3 py-2.5 text-center">STT</th>
                      <th className="px-3 py-2.5">Mã lô / Tên lô</th>
                      <th className="px-3 py-2.5">Thời điểm</th>
                      <th className="px-3 py-2.5">Người thực hiện</th>
                      <th className="px-3 py-2.5 text-right">Số dòng áp dụng</th>
                      <th className="px-3 py-2.5 text-center">Trạng thái</th>
                      {canWrite && <th className="px-3 py-2.5 text-center">Thao tác</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {batches.map((b, idx) => (
                      <tr key={b.id} className="hover:bg-slate-50/80">
                        <td className="px-3 py-2.5 text-center font-medium text-slate-400 tabular-nums">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-semibold text-slate-900">{b.batch_name}</div>
                          <div className="font-mono text-[10px] text-slate-400">{b.id}</div>
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">
                          {new Date(b.created_at).toLocaleString('vi-VN')}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{b.created_by}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-slate-700 tabular-nums">
                          {b.applied_lines}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {b.reverted ? (
                            <StatusBadge tone="danger" label="Đã hoàn tác" />
                          ) : (
                            <StatusBadge tone="success" label="Đã áp dụng" />
                          )}
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2.5 text-center">
                            {!b.reverted && (
                              <Button
                                variant="secondary"
                                size="sm"
                                disabled={revertingBatchId === b.id}
                                onClick={() => handleRevertBatch(b.id)}
                              >
                                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                                {revertingBatchId === b.id ? 'Đang hoàn tác...' : 'Hoàn tác lô'}
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Xác nhận Áp dụng Lô */}
      {isApplyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h3 className="mb-2 text-base font-semibold text-slate-900">
              Xác nhận áp dụng chuẩn hóa tên BOM
            </h3>
            <p className="mb-4 text-xs text-slate-500">
              Bạn đang chuẩn bị áp dụng tên chuẩn cho{' '}
              <strong className="text-blue-700">{selectedLinesList.length} dòng BOM</strong> trên{' '}
              <strong className="text-blue-700">{selectedProductsCount} mã hàng</strong>.
            </p>

            {selectedPriceWarnings.length > 0 && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <div className="flex items-center gap-1.5 font-semibold text-amber-900 mb-1">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Cảnh báo tác động giá ({selectedPriceWarnings.length} dòng):
                </div>
                <ul className="list-disc pl-4 space-y-0.5 text-[11px]">
                  {selectedPriceWarnings.slice(0, 3).map((w, i) => (
                    <li key={i}>
                      <strong>{w.product_code}</strong>: {w.current_name} → {w.proposed_name} ({w.price_warning})
                    </li>
                  ))}
                  {selectedPriceWarnings.length > 3 && (
                    <li>...và {selectedPriceWarnings.length - 3} dòng khác</li>
                  )}
                </ul>
              </div>
            )}

            <div className="space-y-3 text-xs">
              <div>
                <label className="block mb-1 font-medium text-slate-700">Tên lô thay đổi</label>
                <input
                  type="text"
                  value={applyBatchName}
                  onChange={(e) => setApplyBatchName(e.target.value)}
                  placeholder={`Ví dụ: Chuẩn hóa đợt 1 (${selectedLinesList.length} dòng)`}
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-slate-700">Ghi chú (tùy chọn)</label>
                <textarea
                  value={applyNotes}
                  onChange={(e) => setApplyNotes(e.target.value)}
                  rows={2}
                  placeholder="Lý do hoặc thông tin thêm..."
                  className="w-full rounded border border-slate-300 px-2.5 py-1.5 outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setIsApplyModalOpen(false)}>
                Hủy
              </Button>
              <Button variant="primary" disabled={applying} onClick={handleConfirmApply}>
                {applying ? 'Đang thực hiện giao dịch...' : 'Xác nhận áp dụng'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
